import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, ExpectedPayment, ExpectedPaymentPatch, ExpectedPaymentReason } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { errorProp, fieldErrorInsteadOf409 } from "../../components/fieldErrors.js";
import { KiKnopf } from "../../components/meHuellen.js";
import { apiMeldung } from "../../components/vorgangsdetail.js";
import { formatBetrag, formatDatum, formatZeitpunkt } from "../../format.js";
import { berlinerTag } from "../tickets/ticketFormat.js";
import { shortDay } from "../tickets/timelineTexts.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import "./Liquidity.css";

const EP = T.expectedPayment;
/** `NOTE_MAX` der Domäne; web importiert sie nicht. */
const NOTE_MAX = 200;
/** Eine Sperrkollision ist kein fremder Stand: Der Server bittet nur, es noch einmal zu versuchen. */
const LOCK_RETRY: readonly string[] = ["TICKET_LOCK_RETRY"];
const REASONS = Object.keys(T.reason) as ExpectedPaymentReason[];

type HistoryEntry = ExpectedPayment["history"][number];
type Draft = { date: string; reason: ExpectedPaymentReason | ""; note: string };

const NO_DRAFT: Draft = { date: "", reason: "", note: "" };
const draftOf = (v: ExpectedPayment): Draft =>
  (v.expected === null ? NO_DRAFT : { date: v.expected.date, reason: v.expected.reason, note: v.expected.note ?? "" });
const dayOrDash = (day: string | null): string => (day === null ? EP.noValue : formatDatum(day));

/** Was für den Eingang gilt: der gesetzte Termin mit Grund, sonst die Vorgabe. Auch die Zeile im Rechnungsdetail. */
export function expectedPaymentText(v: ExpectedPayment): string {
  return v.expected === null ? EP.lineDefault(formatDatum(v.default.date)) : EP.lineSet(formatDatum(v.expected.date), T.reason[v.expected.reason]);
}

/** Die Vorgabezeile. Die Mindestzahl Belege kennt nur der Server: Unter ihr und beim pünktlichen Zahler ist `delayDays` gleich 0. */
function defaultLine(v: ExpectedPayment): string {
  const { date, delayDays, sampleSize } = v.default, day = formatDatum(date);
  if (v.dueDate === null) return EP.defaultNoDue(day);
  if (delayDays > 0) return EP.defaultDerived(day, shortDay(v.dueDate), delayDays, sampleSize);
  return sampleSize === 0 ? EP.defaultNoHistory(day) : EP.defaultNoDelay(day, sampleSize);
}

function historySide(s: HistoryEntry["from"], defaultDate: string): string {
  return s.date === null || s.reason === null ? EP.historyDefault(formatDatum(defaultDate)) : EP.lineSet(formatDatum(s.date), T.reason[s.reason]);
}

/** Der Verlauf trägt keine Bemerkungstexte (Datenschutz, Ruling Review Aufgabe 0, H5): nur, DASS sie sich geändert hat. */
function History(p: { entries: ExpectedPayment["history"] }): ReactElement | null {
  if (p.entries.length === 0) return null;
  return (
    <section>
      <h3 className="liq__history-title">{EP.history}</h3>
      <ul className="liq__history">
        {p.entries.map((e) => (
          <li key={e.id} className="liq__history-row">
            <span className="liq__history-head">{EP.historyHead(formatZeitpunkt(e.occurredAt), e.userName)}</span>
            {(e.from.date !== e.to.date || e.from.reason !== e.to.reason)
              && <span>{EP.historyChange(historySide(e.from, e.defaultDate), historySide(e.to, e.defaultDate))}</span>}
            {e.noteChanged && <span className="liq__muted">{EP.noteChanged}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * ZAHLUNGSEINGANG ERWARTET (Spec „Dialoge › Erwartungstermin“, 557, Plan P7): Modalpanel rechts, „Speichern“ ist die Bestätigung, drei Eingaben
 * genügen. Leeres Datum heißt: Es gilt die Vorgabe. Bewusst kein Vorbelegen mit ihr, sonst schriebe ein Speichern die heutige Vorgabe als
 * gesetzten Termin fest. Das Panel lädt seinen Stand selbst; die Marke `ifChangedAt` ist `changedAt` der Ansicht, nicht das von `expected`.
 *
 * Ein fremder Stand (409) legt `conflict.current` in die Anzeige, die Eingaben bleiben stehen: „Neu laden“ übernimmt den Serverstand, „Meine
 * Eingaben behalten“ rückt nur die Marke vor. „Nicht vor heute“ prüft es nur am geänderten Datum: Ein inzwischen vergangener Termin lässt
 * sich unverändert speichern (Server: keine Änderung, kein Ereignis). „Auf Vorgabe zurücksetzen“ steht bei der Vorgabezeile, nicht im Fuß:
 * Der hat genau zwei Ausgänge (Hausregel strikte Modalpanels).
 *
 * Eine Rechnung, die nicht mehr offen ist (veraltete Spalte, inzwischen bezahlt), bietet nichts mehr an: Hinweis statt Felder, ein Knopf
 * „Schließen“. Abbrechen reicht den zuletzt gezeigten Stand nach oben (`latest`, `null` ohne Stand): Wer nach einem fremden Stand abbricht,
 * soll in der Zeile dahinter nicht wieder den alten Termin lesen.
 */
export function ExpectedPaymentPanel(p: {
  invoiceId: string; client: Pick<ApiClient, "getExpectedPayment" | "setExpectedPayment">;
  onSaved: (v: ExpectedPayment) => void; onCancel: (latest: ExpectedPayment | null) => void;
}): ReactElement {
  const { invoiceId, client } = p;
  const [view, setView] = useState<ExpectedPayment | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState(NO_DRAFT);
  const [tried, setTried] = useState(false);
  const { bestaetigen } = useBestaetigung();
  const patch = useRef<ExpectedPaymentPatch | null>(null);
  const keepInputs = useRef(false);

  useEffect(() => {
    let stale = false;
    client.getExpectedPayment(invoiceId).then((v) => {
      if (stale) return;
      setView(v);
      setDraft(draftOf(v));
    }, (e: unknown) => { if (!stale) setLoadError(apiMeldung(e, EP.loadFailed)); });
    return () => { stale = true; };
  }, [client, invoiceId]);

  const form = useFormularZustand<ExpectedPayment>({
    speichern: async () => {
      try {
        return await fieldErrorInsteadOf409(() => client.setExpectedPayment(invoiceId, patch.current!), LOCK_RETRY);
      } catch (e) {
        const current = e instanceof ApiError ? e.conflict?.current as ExpectedPayment | undefined : undefined;
        if (current !== undefined) setView(current);
        throw e;
      }
    },
    neuLaden: async () => {
      const keep = keepInputs.current;
      keepInputs.current = false;
      const fresh = await client.getExpectedPayment(invoiceId);
      setView(fresh);
      if (keep) return;
      setDraft(draftOf(fresh));
      setTried(false);
    },
    bekannteFelder: ["expectedPaymentDate", "reason", "note"],
  });

  const start = view === null ? NO_DRAFT : draftOf(view);
  const closed = view !== null && !view.open;
  const dirty = !closed && (draft.date !== start.date || draft.reason !== start.reason || draft.note !== start.note);
  useBeforeUnload(dirty);
  const note = draft.note.trim();
  const dateError = draft.date === "" ? T.dialogs.dateRequired
    : draft.date !== start.date && draft.date < berlinerTag(new Date().toISOString()) ? EP.dateInPast : undefined;
  const reasonError = draft.reason === "" ? EP.reasonRequired : undefined;
  const noteError = draft.reason === "OTHER" && note === "" ? EP.noteRequired : undefined;
  const shown = (server: string | undefined, own: string | undefined): { fehler?: string } => errorProp(server ?? (tried ? own : undefined));

  async function send(values: Omit<ExpectedPaymentPatch, "ifChangedAt">): Promise<void> {
    patch.current = { ...values, ifChangedAt: view?.changedAt ?? null };
    const answer = await form.aktion();
    if (answer !== undefined) p.onSaved(answer);
  }
  async function save(): Promise<void> {
    setTried(true);
    if (dateError !== undefined || draft.reason === "" || noteError !== undefined) return;
    await send({ expectedPaymentDate: draft.date, reason: draft.reason, note: note === "" ? null : note });
  }
  async function reset(fallback: string): Promise<void> {
    if (await bestaetigen({ titel: EP.resetTitle, bestaetigenText: EP.resetConfirm, text: EP.resetText(fallback) })) {
      await send({ expectedPaymentDate: null, reason: null, note: null });
    }
  }
  async function leave(): Promise<void> {
    if (!form.laeuft && await darfAbbrechen(dirty)) p.onCancel(view);
  }

  const body = (v: ExpectedPayment): ReactElement => (
    <div className="liq__mask">
      <dl className="liq__facts">
        <dt>{EP.payer}</dt><dd>{v.payerName}</dd>
        <dt>{EP.invoiceDate}</dt><dd>{dayOrDash(v.invoiceDate)}</dd>
        <dt>{EP.dueDate}</dt><dd>{dayOrDash(v.dueDate)}</dd>
        <dt>{EP.open}</dt><dd className="liq__amount">{formatBetrag(v.openCents / 100, "EUR")}</dd>
      </dl>
      <p className="liq__default">{defaultLine(v)}</p>
      {!v.open && <Hinweis art="info">{EP.notOpen}</Hinweis>}
      {v.open && v.expected !== null && (
        <KiKnopf kompakt className="liq__reset" deaktiviert={form.zustand !== "bereit"} onClick={() => { void reset(formatDatum(v.default.date)); }}>{EP.reset}</KiKnopf>
      )}
      {v.open && <Formular beschriftung="oben" {...form.formularProps}
        hinweis={form.zustand === "fremder-stand" ? (
          <span>
            {form.fremderStand === null || form.fremderStand.changedBy === "" ? EP.conflictAnonymous : EP.conflictBy(form.fremderStand.changedBy)}{" "}
            {v.expected === null ? EP.conflictDefault(formatDatum(v.default.date)) : EP.conflictSet(expectedPaymentText(v))}{" "}
            {EP.conflictChoice}{" "}
            <KiKnopf className="ki-eingaben-behalten" onClick={() => { keepInputs.current = true; void form.neuLaden(); }}>{EP.keepInputs}</KiKnopf>
          </span>
        ) : undefined}>
        <div className="liq__mask">
          <Eingabe art="datum" beschriftung={EP.date} wert={draft.date} pflicht {...shown(form.feldFehler.expectedPaymentDate, dateError)}
            onGeaendert={(e) => setDraft((d) => ({ ...d, date: e.wert }))} />
          <Auswahl beschriftung={EP.reason} wert={draft.reason} {...shown(form.feldFehler.reason, reasonError)}
            onGeaendert={(e) => setDraft((d) => ({ ...d, reason: e.wert as Draft["reason"] }))}>
            <option value="">{EP.chooseReason}</option>
            {REASONS.map((r) => <option key={r} value={r}>{T.reason[r]}</option>)}
          </Auswahl>
          <Eingabe beschriftung={EP.note} mehrzeilig wert={draft.note} pflicht={draft.reason === "OTHER"} maxLength={NOTE_MAX}
            {...shown(form.feldFehler.note, noteError)} onGeaendert={(e) => setDraft((d) => ({ ...d, note: e.wert }))} />
        </div>
      </Formular>}
      <History entries={v.history} />
    </div>
  );

  return (
    <Modal titel={EP.title(view?.invoiceNumber ?? null)} onAbbrechen={() => { void leave(); }} abbrechenGesperrt={form.laeuft}>
      {loadError !== null && <Hinweis art="fehler">{loadError}</Hinweis>}
      {form.meldung !== null && <Hinweis art="fehler">{form.meldung}</Hinweis>}
      {view !== null ? body(view) : loadError === null && <p className="liq__muted" role="status">{T.loading}</p>}
      {closed
        ? <ModalFuss speichernText={EP.close} nurSchliessen onSpeichern={() => { void leave(); }} onAbbrechen={() => { void leave(); }} />
        : <ModalFuss speichernText={T.dialogs.save} speichernGesperrt={view === null || form.zustand !== "bereit"} abbrechenGesperrt={form.laeuft}
            onSpeichern={() => { void save(); }} onAbbrechen={() => { void leave(); }} />}
    </Modal>
  );
}
